/**
 * M4.5 (Juni 2026) – OPS Contract Preview (READ-ONLY).
 *
 * Reine Betreiber-Sicht: wie der Resolver JEDEN Mandanten auflöst (Effective View
 * aus Katalog + Vertrag bzw. Legacy-Mapping). KEINE Writes, KEINE Aktionen, KEINE
 * Bearbeiten-Buttons, KEINE Provider-/Stripe-IDs – nur Anzeige der Allow-list-Felder.
 */
import { useCallback, useEffect, useState } from "react";

import { AlertTriangle, FileText, Loader2, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { opsApi, opsErrorMessage } from "@/lib/opsApi";

const CATEGORY_STYLE = {
  mapped: "bg-emerald-100 text-emerald-800 border-emerald-200",
  excluded: "bg-slate-100 text-slate-600 border-slate-200",
  manual_required: "bg-amber-100 text-amber-900 border-amber-200",
};
const CATEGORY_LABEL = {
  mapped: "Abgebildet",
  excluded: "Ausgeschlossen",
  manual_required: "Manuell nötig",
};

const FILTERS = [
  { key: "all", label: "Alle" },
  { key: "mapped", label: "Nur abgebildet" },
  { key: "excluded", label: "Nur ausgeschlossen" },
  { key: "manual_required", label: "Nur manuell nötig" },
];

function fmtEuro(cents) {
  if (cents == null) return "–";
  try {
    return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(
      cents / 100,
    );
  } catch {
    return `${(cents / 100).toFixed(2)} €`;
  }
}

function Kpi({ id, label, value, tone }) {
  const toneCls =
    tone === "amber"
      ? "text-amber-700"
      : tone === "green"
        ? "text-emerald-700"
        : "text-slate-900";
  return (
    <div
      data-testid={`ops-contract-preview-kpi-${id}`}
      className="rounded-lg border border-slate-200 bg-white px-4 py-3"
    >
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${toneCls}`}>{value ?? "–"}</div>
    </div>
  );
}

export function OpsContractPreviewSection() {
  const [summary, setSummary] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");

  const load = useCallback((activeFilter) => {
    setLoading(true);
    setLoadErr("");
    opsApi
      .contractsPreview(activeFilter)
      .then((data) => {
        setSummary(data.summary || null);
        setCurrentVersion(data.current_catalog_version || null);
        setTenants(data.tenants || []);
      })
      .catch((e) => setLoadErr(opsErrorMessage(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(filter);
  }, [load, filter]);

  return (
    <Card
      className="bg-white border border-slate-200 shadow-sm overflow-hidden"
      data-testid="ops-contract-preview-section"
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-slate-500" />
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">
              Billing → Vertragsvorschau (Resolver)
            </h2>
            <p className="text-xs text-slate-500">
              Read-only: so löst der Resolver jeden Mandanten auf (Katalog + Vertrag bzw.
              Legacy-Mapping). Reine Vorschau – keine Aktionen, keine Vertragsänderung.
            </p>
          </div>
        </div>
        <Button
          data-testid="ops-contract-preview-refresh"
          variant="outline"
          size="sm"
          onClick={() => load(filter)}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Aktualisieren
        </Button>
      </div>

      {/* KPI-Karten */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 px-5 py-4 bg-slate-50/60">
        <Kpi id="total" label="Mandanten" value={summary?.total_tenants} />
        <Kpi id="mapped" label="Abgebildet" value={summary?.mapped} tone="green" />
        <Kpi id="excluded" label="Ausgeschlossen" value={summary?.excluded} />
        <Kpi
          id="manual"
          label="Manuell nötig"
          value={summary?.manual_required}
          tone={summary?.manual_required > 0 ? "amber" : "green"}
        />
        <Kpi id="persisted" label="Verträge persistiert" value={summary?.contracts_persisted} />
      </div>

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-slate-100">
        {FILTERS.map((f) => (
          <Button
            key={f.key}
            data-testid={`ops-contract-preview-filter-${f.key}`}
            variant={filter === f.key ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(f.key)}
            className={filter === f.key ? "bg-slate-900 hover:bg-slate-800 text-white" : ""}
          >
            {f.label}
          </Button>
        ))}
        {currentVersion && (
          <span
            className="ml-auto text-xs text-slate-400"
            data-testid="ops-contract-preview-current-version"
          >
            Aktive Katalogversion: {currentVersion}
          </span>
        )}
      </div>

      {loading && (
        <div
          className="px-5 py-10 flex items-center justify-center text-slate-400"
          data-testid="ops-contract-preview-loading"
        >
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      )}

      {!loading && loadErr && (
        <div className="px-5 py-6" data-testid="ops-contract-preview-error">
          <div className="flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-sm text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {loadErr}
            </p>
            <Button variant="outline" size="sm" onClick={() => load(filter)}>
              Erneut versuchen
            </Button>
          </div>
        </div>
      )}

      {!loading && !loadErr && (
        <div className="overflow-x-auto">
          <Table data-testid="ops-contract-preview-table">
            <TableHeader>
              <TableRow>
                <TableHead>Mandant</TableHead>
                <TableHead>Legacy-Tarif</TableHead>
                <TableHead>Vertragstyp</TableHead>
                <TableHead>Produkt</TableHead>
                <TableHead>Katalogversion</TableHead>
                <TableHead className="text-right">Preis (mtl.)</TableHead>
                <TableHead className="text-right">Preis (jährl.)</TableHead>
                <TableHead className="text-right">Mitarbeiter</TableHead>
                <TableHead>Kategorie</TableHead>
                <TableHead>Warnungen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenants.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    className="text-center text-slate-400 py-8"
                    data-testid="ops-contract-preview-empty"
                  >
                    Keine Mandanten für diesen Filter.
                  </TableCell>
                </TableRow>
              )}
              {tenants.map((t, idx) => {
                const eff = t.effective;
                return (
                  <TableRow
                    key={t.tenant_id || idx}
                    data-testid={`ops-contract-preview-row-${idx}`}
                  >
                    <TableCell className="text-sm">
                      <div className="font-medium text-slate-900">{t.name || "–"}</div>
                      <div className="text-xs text-slate-400 font-mono">{t.tenant_id}</div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">{t.legacy_plan || "–"}</TableCell>
                    <TableCell className="text-sm text-slate-700">
                      {eff ? eff.contract_type : "–"}
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">
                      {eff ? eff.product_id || "–" : "–"}
                    </TableCell>
                    <TableCell className="text-xs font-mono text-slate-500">
                      {eff ? eff.catalog_version || "–" : "–"}
                    </TableCell>
                    <TableCell className="text-right text-sm whitespace-nowrap">
                      {eff ? fmtEuro(eff.effective_monthly_net_cents) : "–"}
                    </TableCell>
                    <TableCell className="text-right text-sm whitespace-nowrap">
                      {eff ? fmtEuro(eff.effective_yearly_net_cents) : "–"}
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {eff ? eff.billable_quantity ?? "–" : "–"}
                    </TableCell>
                    <TableCell className="text-sm">
                      <Badge
                        variant="outline"
                        data-testid={`ops-contract-preview-category-${t.category}`}
                        className={`rounded-md font-medium px-2.5 py-1 text-xs border ${
                          CATEGORY_STYLE[t.category] || "bg-slate-100 text-slate-700 border-slate-200"
                        }`}
                      >
                        {CATEGORY_LABEL[t.category] || t.category}
                      </Badge>
                      {t.mapping_status && t.category !== "mapped" && (
                        <div className="mt-1 text-xs text-slate-400 font-mono">
                          {t.mapping_status}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">
                      {eff && eff.warnings && eff.warnings.length > 0
                        ? eff.warnings.join(", ")
                        : "–"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}
